import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { Governorate, District, TimeSlot, ServiceInputs, OrderAddOn } from '@/lib/types';

export function useGovernorates() {
  return useQuery({
    queryKey: ['/governorates'],
    queryFn: async (): Promise<Governorate[]> => {
      const response = await apiClient.get('/governorates?activeOnly=true');
      if (!response.ok) {
        throw new Error('Failed to fetch governorates');
      }
      return response.json();
    },
  });
}

export function useDistricts(governorateId: string | undefined) {
  return useQuery({
    queryKey: ['/districts', governorateId],
    queryFn: async (): Promise<District[]> => {
      const response = await apiClient.get(`/districts/by-governorate/${governorateId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch districts');
      }
      return response.json();
    },
    enabled: !!governorateId,
  });
}

export function useDeliveryFee(districtId: string | undefined) {
  return useQuery({
    queryKey: ['/districts', districtId, 'delivery-fee'],
    queryFn: async (): Promise<{ deliveryFee: number }> => {
      const response = await apiClient.get(`/districts/${districtId}/delivery-fee`);
      if (!response.ok) {
        throw new Error('Failed to fetch delivery fee');
      }
      return response.json();
    },
    enabled: !!districtId,
  });
}

export function useTimeSlots(date: string | undefined) {
  return useQuery({
    queryKey: ['/time-slots', date],
    queryFn: async (): Promise<TimeSlot[]> => {
      const response = await apiClient.get(`/time-slots${date ? `?date=${date}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch time slots');
      }
      return response.json();
    },
    enabled: !!date,
  });
}

interface ServiceItemForSlots {
  serviceId: string;
  quantity?: number;
  unit?: number;
  inputs?: ServiceInputs;
  addOns?: OrderAddOn[];
}

export function useTimeSlotsForServices(date: string | undefined, items: ServiceItemForSlots[]) {
  const itemsKey = JSON.stringify(items.map(i => ({ s: i.serviceId, q: i.quantity, u: i.unit, inp: i.inputs, a: i.addOns })));

  return useQuery({
    queryKey: ['/time-slots/available-for-services', date, itemsKey],
    queryFn: async (): Promise<TimeSlot[]> => {
      const response = await apiClient.post('/time-slots/available-for-services', {
        date,
        items,
      });
      if (!response.ok) {
        throw new Error('Failed to fetch time slots');
      }
      return response.json();
    },
    enabled: !!date && items.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useAvailableTimeSlots(date: string | undefined) {
  return useQuery({
    queryKey: ['/time-slots/available', date],
    queryFn: async (): Promise<TimeSlot[]> => {
      const response = await apiClient.get(`/time-slots/available${date ? `?date=${date}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch available time slots');
      }
      return response.json();
    },
    enabled: !!date,
    staleTime: 0,
    gcTime: 0,
  });
}

// Service-specific availability: GET /time-slots/available?date=YYYY-MM-DD&serviceId=<id>
export function useAvailableTimeSlotsForService(date: string | undefined, serviceId: string | undefined) {
  return useQuery({
    queryKey: ['/time-slots/available', date, 'service', serviceId],
    queryFn: async (): Promise<TimeSlot[]> => {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (serviceId) params.set('serviceId', serviceId);
      const response = await apiClient.get(`/time-slots/available?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch available time slots');
      }
      return response.json();
    },
    enabled: !!date && !!serviceId,
    staleTime: 0,
    gcTime: 0,
  });
}
